import {ColorObjectStrategy} from "../../../_types/ColorObjectStrategy";
import type {ColorProperty} from "../../../_types/ColorProperty";
import type {Point} from "../../../_types/Point";
import type {SceneObjectWithColorData} from "../../../_types/SceneObjectWithColorData";
import type {Size} from "../../../_types/Size";
import type {WebGLRenderObject} from "../../../_types/WebGLRenderObject";
import {getColorPaletteFromColorData} from "../../../helpers/getColorPaletteFromColorData";
import {getRandomNumberBetween} from "../../../helpers/getRandomNumberBetween";
import type {ObjectPipelinePass} from "../../ObjectPipeline";
import {PositionColorObjectStrategy} from "./strategies/PositionColorObjectStrategy";
import {RandomColorObjectStrategy} from "./strategies/RandomColorObjectStrategy";

// The random offset used to pick different colors based on position,
// this makes the colors based on position different each time.
const RANDOM_RENDER_OBJECT_OFFSET = 16;

export class ColorObjectPass implements ObjectPipelinePass {

    constructor(
        private sceneSize: Size,
        private options: ColorProperty) {}
    // eslint-disable-next-line prefer-arrow/prefer-arrow-functions
    public apply(
        index: number,
        renderObject: WebGLRenderObject,
        sceneObjects: Array<SceneObjectWithColorData>
    ): void {

        const { mode, maxColors } = this.options;

        // Blur mode uses the rendered `WebGLRenderObjects` as mask.
        // Since we don't need any color we just ignore this pass.
        if (mode === "blur") {
            return;
        }

        // Can't color objects based on anything
        if (sceneObjects.length === 0) {
            return;
        }

        const closestSceneObject = getClosestSceneObject({
            x: renderObject.x + getRandomNumberBetween(
                -RANDOM_RENDER_OBJECT_OFFSET,
                RANDOM_RENDER_OBJECT_OFFSET),
            y: renderObject.y + getRandomNumberBetween(
                -RANDOM_RENDER_OBJECT_OFFSET,
                RANDOM_RENDER_OBJECT_OFFSET)
        }, sceneObjects);

        const colorObjectStrategy = getColorObjectStrategyFromColorMode(this.options);

        if (maxColors && !closestSceneObject.data.palettes[maxColors]) {
            closestSceneObject.data.palettes[maxColors] = getColorPaletteFromColorData(
                closestSceneObject.data, maxColors);
        }

        colorObjectStrategy.color(renderObject, closestSceneObject);
    }
}

const getClosestSceneObject = (
    { x, y }: Point,
    sceneObjects: Array<SceneObjectWithColorData>
): SceneObjectWithColorData => {

    let closestSceneObject = sceneObjects[0];
    let closestSceneObjectDistance = getSceneObjectDistanceToPoint(closestSceneObject, { x, y });

    for (const sceneObject of sceneObjects) {
        const distance = getSceneObjectDistanceToPoint(sceneObject, { x, y });
        if (distance < closestSceneObjectDistance) {
            closestSceneObject = sceneObject;
            closestSceneObjectDistance = distance;
        }
    }

    return closestSceneObject;
};

const getSceneObjectDistanceToPoint = (
    sceneObject: SceneObjectWithColorData,
    { x, y }: Point
): number => {

    const left = sceneObject.x;
    const right = sceneObject.x + sceneObject.width;
    const top = sceneObject.y;
    const bottom = sceneObject.y + sceneObject.height;

    const dx = Math.max(0, Math.max(left - x, x - right));
    const dy = Math.max(0, Math.max(top - y, y - bottom));

    return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
};

const colorModeToColorObjectStrategyMap: Record<string, any> = {
    "random": RandomColorObjectStrategy,
    "position": PositionColorObjectStrategy
};

const getColorObjectStrategyFromColorMode = (colorProperty: ColorProperty): ColorObjectStrategy => {

    const ColorObjectStrategy = colorModeToColorObjectStrategyMap[colorProperty.mode];

    if (!ColorObjectStrategy) {
        throw new Error(`No color object strategy for color mode '${colorProperty.mode}'`);
    }

    return new ColorObjectStrategy(colorProperty.maxColors);
};